import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Account } from 'projects/internal/r1-component-library/src/lib/components/appcloud-login/appcloud-credentials/appcloud-credentials.model';
import { CountResponse } from 'projects/internal/r1-component-library/src/lib/models/appcloud/count-response';
import { PagedResponse } from 'projects/internal/r1-component-library/src/lib/models/appcloud/paged-response.model';
import { AdminHttpService } from './utils/admin-http.service';
import { QueryBuilderService } from './utils/query-builder.service';
import { PagedSearch } from '../models/paged-search.model';
import { ChartSeries } from '../models/account-creation-point.model';

export type AccountPatch = Partial<Pick<Account, 'name' | 'description' | 'sfAccountId'>>;

/**
 * Admin-portal access to `/appcloud/admin/accounts` (`admin-account-controller`).
 * Search matches `name contains` or `id equals` (crossFieldSearch).
 *
 * `/admin/organizations` is an identical controller (same `PagedResponse<Account>`); the portal
 * uses `/admin/accounts` until the backend settles on one.
 */
@Injectable({
    providedIn: 'root',
})
export class ManageAccountsService {
    readonly #http = inject(AdminHttpService);
    readonly #queryBuilder = inject(QueryBuilderService);

    readonly #accountsUrl = `${this.#http.adminUrl}/accounts`;

    findAccounts(search: PagedSearch): Observable<PagedResponse<Account>> {
        return this.#http.get<PagedResponse<Account>>(this.#accountsUrl, {
            process: 'ManageAccountsService.findAccounts',
            message: 'Loading accounts...',
            errorMessage: 'An error occurred while retrieving accounts info. Please try again later.',
            params: this.#queryBuilder.pagedParams(search, [
                ['name', 'contains'],
                ['id', 'equals'],
            ]),
        });
    }

    /** Accounts by id, used for the "Switch Account" list (`accountIds` claim of the JWT). */
    findAccountsByIds(ids: number[]): Observable<Account[]> {
        if (!ids.length) {
            return of([]);
        }
        return this.#http
            .get<PagedResponse<Account>>(this.#accountsUrl, {
                process: 'ManageAccountsService.findAccountsByIds',
                message: 'Loading your accounts...',
                errorMessage: 'An error occurred while retrieving your accounts. Please try again later.',
                params: new HttpParams({
                    fromObject: { ids: ids.join(','), page: 0, pageSize: Math.max(ids.length, 1), sortField: 'name' },
                }),
            })
            .pipe(map(page => page?.content ?? []));
    }

    getAccount(id: number): Observable<Account> {
        return this.#http.get<Account>(`${this.#accountsUrl}/${id}`, {
            process: 'ManageAccountsService.getAccount',
            message: 'Loading account...',
            errorMessage: 'An error occurred while retrieving the account. Please try again later.',
        });
    }

    patchAccount(id: number, account: AccountPatch): Observable<Account> {
        return this.#http.patch<Account>(`${this.#accountsUrl}/${id}`, account, {
            process: 'ManageAccountsService.patchAccount',
            message: 'Updating account...',
            errorMessage: 'An error occurred while sending data to Relationship One. Please try again later.',
        });
    }

    /** `DELETE /appcloud/admin/accounts/{id}` (UXPin row menu "Delete"). */
    deleteAccount(id: number): Observable<void> {
        return this.#http.delete<void>(`${this.#accountsUrl}/${id}`, {
            process: 'ManageAccountsService.deleteAccount',
            message: 'Deleting account...',
            errorMessage: 'An error occurred while deleting the account. Please try again later.',
        });
    }

    /** Non-R1 account count, optionally limited to accounts created in the last `days`. */
    countAccounts(days: number | null): Observable<number> {
        const params = this.#queryBuilder.createdSinceParams(
            days,
            new HttpParams({ fromObject: { ownedByR1: 'false' } })
        );
        return this.#http
            .get<CountResponse[]>(`${this.#accountsUrl}/counts`, {
                process: 'ManageAccountsService.countAccounts',
                message: 'Loading account count...',
                errorMessage: 'An error occurred while retrieving the account count. Please try again later.',
                params,
            })
            .pipe(map(counts => counts?.[0]?.count ?? 0));
    }

    /**
     * Accounts created per week for the "New Account Creation" chart.
     *
     * TODO(backend): confirm the `groupBy` value that buckets `createdAt` by week
     * (`/admin/accounts/counts?groupBy=...` documents the parameter, not its values).
     */
    accountCreationSeries(days: number): Observable<ChartSeries[]> {
        const params = this.#queryBuilder.createdSinceParams(
            days,
            new HttpParams({ fromObject: { ownedByR1: 'false', groupBy: 'createdAtWeek' } })
        );
        return this.#http
            .get<CountResponse[]>(`${this.#accountsUrl}/counts`, {
                process: 'ManageAccountsService.accountCreationSeries',
                message: 'Loading account creation trend...',
                errorMessage: 'An error occurred while retrieving the account creation trend. Please try again later.',
                params,
            })
            .pipe(
                map(counts => [
                    {
                        name: 'New accounts',
                        series: (counts ?? []).map(count => ({ name: count.key ?? '', value: count.count ?? 0 })),
                    },
                ])
            );
    }
}
